/**
 * Escapes HTML special characters to prevent XSS when injecting user-supplied
 * strings into HTML templates (e.g. document.write / print popups).
 *
 * Usage:  import { escapeHtml } from '../../utils/sanitize';
 *         const e = escapeHtml;
 *         html = `<div>${e(userValue)}</div>`;
 */
export function escapeHtml(value) {
  if (value == null) return '';
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

// ─── Input validation helpers ─────────────────────────────────────────────────

const SCRIPT_RE        = /(<\s*script[\s\S]*?>|<\s*\/\s*script[\s\S]*?>|javascript\s*:)/i;
const EVENT_HANDLER_RE = /\bon\w+\s*=/i;   // onerror=, onclick=, onmouseover=, etc.
const HTML_TAG_RE      = /<[^>]*>/;

/**
 * Returns an error message string if `val` contains HTML/script injection,
 * or null if the value is safe.
 *
 * Usage in onChange:
 *   setFieldErrors(fe => ({ ...fe, notes: validateFreeText(e.target.value) }));
 */
export function validateFreeText(val) {
  if (!val) return null;
  if (SCRIPT_RE.test(val))        return 'Must not contain scripts or JavaScript';
  if (EVENT_HANDLER_RE.test(val)) return 'Must not contain event handlers (e.g. onerror=, onclick=)';
  if (HTML_TAG_RE.test(val))      return 'Must not contain HTML tags (e.g. <b>, <script>)';
  return null;
}

/**
 * Validates multiple free-text fields in a form object at once.
 * Returns an object with { fieldName: errorMessage } for any field that fails.
 *
 * Usage before form submit:
 *   const errs = validateFormTextFields(form, ['name', 'address', 'notes']);
 *   if (Object.keys(errs).length) { setFieldErrors(errs); return; }
 */
export function validateFormTextFields(form, fieldNames) {
  const errors = {};
  fieldNames.forEach(name => {
    const err = validateFreeText(form[name]);
    if (err) errors[name] = err;
  });
  return errors;
}

// ─── Notification link helpers ────────────────────────────────────────────────

/**
 * Returns a safe `tel:` href string, or null if the phone is empty/invalid.
 * Only allows digits, +, spaces, hyphens, parentheses — blocks javascript: injection.
 * Usage:  const href = safePhoneHref(phone);  if (href) <a href={href}>
 */
export function safePhoneHref(phone) {
  if (!phone) return null;
  // Strip whitespace, then allow only: digits, +, space, -, (, )
  const clean = String(phone).replace(/[^\d+\s\-()]/g, '').trim();
  if (!clean || clean.replace(/\D/g, '').length < 5) return null; // too short to be real
  return `tel:${clean}`;
}

/**
 * Returns a digits-only E.164-ready phone string for wa.me URLs, or null if invalid.
 * Prepends country code 91 for 10-digit Indian numbers.
 */
export function safePhoneE164(phone) {
  if (!phone) return null;
  const bare = String(phone).replace(/\D/g, '');
  if (bare.length < 5) return null;
  return bare.length === 10 ? `91${bare}` : bare;
}

/**
 * Strips a display string to safe plain text for use inside WhatsApp messages.
 * Removes HTML tags, URLs, and angle brackets that could be used for social engineering.
 */
export function safeMessageText(val) {
  if (!val) return '';
  return String(val)
    .replace(/<[^>]*>/g, '')            // strip HTML tags
    .replace(/https?:\/\/\S+/gi, '')    // strip URLs
    .replace(/[<>]/g, '')               // strip remaining angle brackets
    .trim();
}
